import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import FileService from "../../backend/service/FileService";
import {useQuery} from "react-query";
import {Box, Icon, IconButton, HStack} from "@chakra-ui/react";
import {
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
} from "@chakra-ui/react"
import {Link as RouterLink} from "react-router-dom";
import {FiHome, FiZoomIn, FiZoomOut} from "react-icons/fi";
import {MdArrowBack, MdArrowForward} from "react-icons/md";
import {RiDualSim1Line, RiDualSim2Line} from "react-icons/ri";
import {Document, Page} from "react-pdf";
import './PdfViewer.css'

interface PdfViewerProps {
    uuid: string
    token: string
}


export default function PdfViewer({uuid, token}: PdfViewerProps) {
    const {t} = useTranslation();
    const [scale, setScale] = useState<number>(1.0);
    const [numPages, setNumPages] = useState<number>(0);
    const [diploma, setDiploma] = useState<any>(undefined)
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [dual, setDual] = useState<boolean>(false);

    const fileService = new FileService(token);

    const {isLoading, error,} = useQuery('getDiplomaFile', () => {
            if (uuid && token !== '') {
                return fileService.getDiploma(uuid).then(
                    (response: { data: any; }) => setDiploma(response.data)
                )
            } else {
                return undefined
            }
        }
    )

    if (isLoading) return <p>{t('pdf_viewer.loading')}...</p>
    if (error) {
        const queryError = error as Error
        return (<p>{t('pdf_viewer.error')}: {queryError.message}</p>)
    }

    function goNext() {
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1)
        }
    }

    function goPrevious() {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1)
        }
    }

    function larger() {
        if (scale <= 1.8) {
            setScale(scale + 0.2)
        }
    }

    function smaller() {
        if (scale >= 0.6) {
            setScale(scale - 0.2)
        }
    }

    function changeLayout() {
        if (dual) {
            setDual(false)
        } else {
            setDual(true)
        }
    }

    function onPageChange(_: string, valueAsNumber: number) {
        if (valueAsNumber > numPages) {
            setPageNumber(numPages)
        } else if (valueAsNumber < 1) {
            setPageNumber(1)
        } else {
            setPageNumber(valueAsNumber)
        }
    }

    interface onDocumentLoadSuccessProps {
        numPages: number
    }

    function onDocumentLoadSuccess({numPages}: onDocumentLoadSuccessProps) {
        setPageNumber(1)
        setNumPages(numPages);
    }

    return (
        <Box>
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                <HStack spacing="8px">
                    <p>{t('pdf_viewer.page')}</p>
                    <NumberInput width="72px" size="md" onChange={onPageChange}
                                 defaultValue={1}
                                 value={pageNumber}
                                 inputMod={"numeric"}
                                 variant={"outline"}
                                 min={1} max={numPages}>
                        <NumberInputField/>
                        <NumberInputStepper>
                            <NumberIncrementStepper/>
                            <NumberDecrementStepper/>
                        </NumberInputStepper>
                    </NumberInput>
                    <p> {t('pdf_viewer.of')} {numPages} </p>
                </HStack>
            </div>
            <RouterLink to={`/`}>
                <IconButton m="1" aria-label="Return home" icon={<Icon as={FiHome}/>}/>
            </RouterLink>
            <IconButton onClick={goPrevious} m="1" aria-label="Previous page" isDisabled={pageNumber === 1}
                        icon={<Icon as={MdArrowBack}/>}/>
            <IconButton onClick={goNext} m="1" aria-label="Next page" isDisabled={pageNumber === numPages}
                        icon={<Icon as={MdArrowForward}/>}/>
            <IconButton onClick={larger} m="1" aria-label="Larger view" isDisabled={scale >= 1.9}
                        icon={<Icon as={FiZoomIn}/>}/>
            <IconButton onClick={smaller} m="1" aria-label="Smaller view" isDisabled={scale <= 0.45}
                        icon={<Icon as={FiZoomOut}/>}/>
            <IconButton onClick={changeLayout} m="1" aria-label="Dual page"
                        icon={dual ? <Icon as={RiDualSim1Line}/> : <Icon as={RiDualSim2Line}/>}/>
            <div onClick={() => goNext()} onContextMenu={e => {
                e.preventDefault();
                goPrevious()
            }
            } style={{display: 'flex', justifyContent: 'center'}}>
                <Document
                    file={diploma}
                    onLoadSuccess={onDocumentLoadSuccess}
                    className="pdf-container"
                    loading="Przetwarzanie pliku..."
                    noData="Oczekiwanie na plik..."
                >
                    <div style={{display: 'flex', flexDirection: 'row'}}>
                        <Page loading="Przygotowanie strony..." renderTextLayer={false} scale={scale} pageNumber={pageNumber}/>
                        {(dual && (pageNumber < numPages)) ?
                            <Page
                                loading="Przygotowanie strony..."
                                renderTextLayer={false}
                                scale={scale}
                                pageNumber={pageNumber + 1}/> : null}
                    </div>
                </Document>
            </div>
        </Box>
    )
}